<svelte:options />

<script>

    import { onMount, createEventDispatcher } from 'svelte';
    import { floatingMenuRowTypes } from '@repo/config/Global';
    import {
        focusOnFirst,
        focusNextElement,
        slugify,
        getFocusableElements,
        focusPreviousElement,
    } from '@repo/config/Utils';
    import FloatingMenuSectionTitleRow from './FloatingMenuSectionTitleRow.svelte';
    import FloatingMenuSection from './FloatingMenuSection.svelte';
    /**
     * @typedef {Object} Props
     * @property {any} [items]
     * @property {any} top
     * @property {any} [left]
     * @property {any} [bottom]
     * @property {any} [right]
     * @property {string} [size]
     * @property {any} [data]
     * @property {any} [id]
     * @property {boolean} [noAriaRole]
     * @property {boolean} [ariaCheckboxGroup]
     * @property {boolean} [ariaListBox]
     * @property {boolean} [asMenu]
     * @property {string} [as]
     * @property {boolean} [allowTabbing]
     * @property {any} [legend]
     * @property {import('svelte').Snippet} [children]
     */
    let {
        items = [],
        top,
        left = null,
        bottom = null,
        right = null,
        size = 'full',
        data = {},
        id = null,
        noAriaRole = false,
        ariaCheckboxGroup = false,
        ariaListBox = false,
        asMenu = false,
        as = 'menu',
        allowTabbing = false,
        legend = null,
        children = null,
        close = () => {},
    } = $props();

    let innerWidth = $state(), innerHeight = $state();
    let menu = $state();

    const dispatch = createEventDispatcher();

    function handleMenuRowClick(e) {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (e.detail) {
            dispatch('click', {
                action: e.detail.action || e.detail,
                data,
                originalDataSet: e.detail.dataSet || {},
                label: e.detail.label,
            });
        } else {
            dispatch('click', e);
        }
    }

    function handleKeyDown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopImmediatePropagation();

            close && close();

            focusPreviousElement('.floating-menu');
        } else if (
            (ariaListBox || ariaCheckboxGroup) &&
            (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Tab') &&
            !allowTabbing
        ) {
            e.preventDefault();
            e.stopImmediatePropagation();

            if (e.key === 'Tab') {
                close && close();

                if (ariaListBox) {
                    focusNextElement('.floating-menu');
                }

                return;
            }

            let allFocusable = Array.from(
                getFocusableElements(
                    e.target.closest('[role="group"]')?.parentElement
                )
            );

            // Remove all input elements from the list
            allFocusable = allFocusable.filter((el) => el.tagName !== 'INPUT');

            const index = allFocusable.indexOf(e.target);

            const key = e.key === 'ArrowDown' ? 1 : -1;
            const nextIndex = index + key;

            if (nextIndex >= 0 && nextIndex < allFocusable.length) {
                allFocusable[nextIndex]?.focus();
            } else {
                console.warn('No next focusable element');
            }
        } else if (
            (ariaListBox || ariaCheckboxGroup) &&
            (e.key === 'Enter' || e.key === ' ')
        ) {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.target.click();
        }
    }

    function handleMouseDown(e) {
        // Keep focus on the active row when pressing non-interactive parts
        const interactive = e.target.closest(
            'li, a, button, input, label, [tabindex]'
        );
        if (!interactive || !menu?.contains(interactive)) {
            e.preventDefault();
        }
    }

    function lossOfFocus(e) {
        // relatedTarget is null when focus falls to <body>
        const movedTo = e.relatedTarget;
        if (!movedTo || !menu || menu.contains(movedTo)) return;

        // Deferred so a trigger button's own click toggle wins the race
        setTimeout(() => {
            if (menu && !menu.contains(document.activeElement)) {
                close && close();
                focusNextElement('.floating-menu');
            }
        }, 200);
    }

    function getGroupedSections() {
        const sections = [];
        let currentSection = null;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type === floatingMenuRowTypes.SECTION_TITLE) {
                if (currentSection) {
                    sections.push(currentSection);
                }
                currentSection = { title: items[i].label, items: [] };
            } else {
                if (!currentSection) {
                    currentSection = { title: null, items: [] };
                }
                currentSection.items.push(items[i]);
            }
        }
        if (currentSection) {
            sections.push(currentSection);
        }
        return sections;
    }

    onMount(() => {
        if (top) menu.style.top = top;
        if (left) menu.style.left = left;
        if (bottom) menu.style.bottom = bottom;
        if (right) menu.style.right = right;

        // override if out of bounds
        const b = menu.getBoundingClientRect();
        if (b.left < 0) {
            menu.style.right = '';
            menu.style.left = '1rem';
        }
        if (b.right > innerWidth) {
            menu.style.left = '';
            menu.style.right = '1rem';
        }
        if (b.top < 0) {
            menu.style.bottom = '';
            menu.style.top = '1rem';
        }
        if (b.bottom > innerHeight) {
            menu.style.top = '';
            menu.style.bottom = '1rem';
        }

        // Find all focusable elements within the menu
        focusOnFirst('.floating-menu');

        // Close on presses outside the menu (same method as DropDown).
        function handlePointerDownOutside(e) {
            const boundary = menu?.parentElement || menu;
            if (boundary && !boundary.contains(e.target)) {
                close && close();
            }
        }

        window.addEventListener('pointerdown', handlePointerDownOutside, true);

        return () => {
            window.removeEventListener(
                'pointerdown',
                handlePointerDownOutside,
                true
            );
        };
    });

    export {
        items,
        top,
        left,
        bottom,
        right,
        size,
        data,
        id,
        noAriaRole,
        ariaCheckboxGroup,
        ariaListBox,
        asMenu,
        as,
        allowTabbing,
        legend,
    };
</script>

<svelte:window bind:innerWidth bind:innerHeight />
<svelte:element
    this={as}
    bind:this={menu}
    {id}
    class="floating-menu interactive"
    style={size === 'small'
        ? 'transform:scale(0.8); transform-origin:top left;'
        : ''}
    onkeydown={handleKeyDown}
    onmousedown={handleMouseDown}
    onfocusout={lossOfFocus}
    role={ariaListBox && getGroupedSections().length > 1 ? 'listbox' : null}
    data-testid="floating-menu"
>
    {@render children?.()}
    {#if getGroupedSections().length === 1 && getGroupedSections()[0].title === null && as === 'ul'}
        {@const section = getGroupedSections()[0]}
        <FloatingMenuSection
            on:click={handleMenuRowClick}
            items={section.items}
            {noAriaRole}
            {ariaListBox}
            {asMenu}
        />
    {:else if getGroupedSections().length === 1 && getGroupedSections()[0].title === null}
        {@const section = getGroupedSections()[0]}
        <svelte:element
            this={ariaListBox || ariaCheckboxGroup || asMenu ? 'ul' : 'div'}
            role={ariaListBox ? 'listbox' : null}
        >
            <FloatingMenuSection
                on:click={handleMenuRowClick}
                items={section.items}
                {noAriaRole}
                {ariaListBox}
                {asMenu}
            />
        </svelte:element>
    {:else}
        {#each getGroupedSections() as section}
            <svelte:element
                this={(ariaListBox || asMenu) && as === 'ul' ? 'li' : 'div'}
                role={ariaCheckboxGroup ? 'group' : null}
                aria-labelledby={section.title
                    ? `${slugify(section.title)}-section-legend`
                    : null}
            >
                {#if ariaCheckboxGroup}
                    <fieldset>
                        {#if section.title}
                            <legend>
                                {section.title}
                            </legend>
                        {/if}
                        <svelte:element
                            this={ariaListBox || ariaCheckboxGroup || asMenu
                                ? 'ul'
                                : 'div'}
                            role={ariaListBox ? 'listbox' : null}
                        >
                            <FloatingMenuSection
                                on:click={handleMenuRowClick}
                                items={section.items}
                                {noAriaRole}
                                {ariaListBox}
                                {asMenu}
                            />
                        </svelte:element>
                    </fieldset>
                {:else}
                    {#if section.title}
                        <FloatingMenuSectionTitleRow
                            label={section.title}
                            as={ariaListBox || ariaCheckboxGroup || asMenu
                                ? 'div'
                                : 'li'}
                        />
                    {/if}
                    <svelte:element
                        this={ariaListBox || ariaCheckboxGroup || asMenu
                            ? 'ul'
                            : 'div'}
                        role={ariaListBox ? 'listbox' : null}
                    >
                        <FloatingMenuSection
                            on:click={handleMenuRowClick}
                            items={section.items}
                            {noAriaRole}
                            {ariaListBox}
                            {asMenu}
                        />
                    </svelte:element>
                {/if}
            </svelte:element>
        {/each}
    {/if}
</svelte:element>

<style>
    .floating-menu {
        position: fixed;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        background-color: #333;
        box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.25);
        border-radius: 0.25rem;
        border: 1px solid rgba(255, 255, 255, 0.03);
        z-index: 90000;
        max-height: min(80vh, 500px);
        overflow-y: auto;
    }

    :global(.floating-menu .checkbox label) {
        font-size: inherit !important;
        text-transform: inherit !important;
        font-weight: inherit !important;
        color: inherit !important;
    }

    .floating-menu legend {
        padding-bottom: 0.5rem;
        margin: 1.25rem 1.5rem 0.75rem 1rem;
        text-transform: uppercase;
        font-weight: 600;
        font-size: 0.8rem;
        color: rgb(255, 206, 44);
        border-bottom: 1px solid rgb(255, 206, 44);
        width: calc(100% - 2.5rem);
    }
</style>
